import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usePreloadChunk } from "@/lib/lazy-chunk";
import { CameraScanner } from "@/lib/scan-ui/lazy-camera";
import { ArrowLeft, Camera, Check, Truck, X } from "lucide-react";
import React, { Suspense, useCallback, useRef, useState } from "react";
import { toast } from "sonner";

import * as shipment from "@/api/shipment";
import { Button } from "@/components/ui/button";
import { groupSscc } from "@/lib/boxes";
import { useBackGuard } from "@/lib/scan-ui/back-guard";
import { Dialog } from "@/lib/scan-ui/overlays";
import { errorDetail } from "@/lib/api-errors";
import { apiErrorText, LoadState, useGoBack } from "@/lib/scan-ui/screen";
import { useScanCapture } from "@/lib/use-scan-capture";
import { cn } from "@/lib/utils";
import { ScanBanner, ScanBannerSlot } from "@/pages/fbs/ScanBanner";

// ⚠️ Здесь сканируют ТОЛЬКО этикетку паллеты, а она печатается в QR. Дефолтный
// набор символик — товарный (ЧЗ-DataMatrix и EAN13), и QR в него не входит:
// камера просто не видела код, ошибки при этом не было и показать было нечего.
// Ровно так это и выглядело у кладовщиков — «навожу, а он не читает».
import { QR_FORMATS } from "@/lib/scan-formats";

const KEY = ["shipment-session"];

const ERROR_TITLE: Record<shipment.ShipmentErrorCode, string> = {
  not_found: "Паллета не найдена",
  duplicate: "Уже погружено",
  order_pending: "Сначала догрузите заказ",
  not_checked: "Проверка заказа не завершена",
  closed: "Смена завершена",
  busy: "Смену завершают",
  already_posted: "Документ уже проведён",
  complete_failed: "Смена не завершена",
  error: "Скан не принят",
};

type Hero =
  | { kind: "idle" }
  | { kind: "ok"; pallet: shipment.ScannedPallet }
  | { kind: "error"; code: shipment.ShipmentErrorCode; text: string };

/**
 * Проверка отгрузки: кладовщик грузит машину и сканирует этикетки паллет.
 *
 * Главное на экране — СПИСОК набранных заказов с прогрессом «2 из 5 паллет»:
 * по нему видно, что ещё нести. Результат скана — плашка поверх списка, она не
 * прячет список и не исчезает сама.
 *
 * Правило «догрузи заказ целиком, потом бери следующий» держит бэкенд; экран
 * показывает недогруженный заказ первым и подсвечивает его.
 */
export function ShipmentPage(): React.ReactElement {
  // Чанк камеры тянем при ВХОДЕ: устаревший чанк после выката
  // перезагружает страницу, и делать это в момент тапа «Сканировать»
  // значит прерывать работу ровно посередине.
  usePreloadChunk(CameraScanner);
  const qc = useQueryClient();
  const goBack = useGoBack("/");
  const [hero, setHero] = useState<Hero>({ kind: "idle" });
  const [camera, setCamera] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [done, setDone] = useState<shipment.CompleteResult | null>(null);

  const sessionQ = useQuery({ queryKey: KEY, queryFn: shipment.getSession });
  // 409 на загрузке = смену начали с подключением к ДРУГОЙ базе 1С. Смена одна
  // на портал, и без выхода отсюда отгрузка заперта у всех — поэтому здесь
  // единственная кнопка, которая закрывает смену, не обращаясь в 1С.
  const wrongBase =
    (sessionQ.error as { response?: { status?: number } } | null)?.response?.status === 409;
  const abandon = useMutation({
    mutationFn: shipment.abandonSession,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      toast.success("Смена закрыта — можно начинать заново");
    },
    onError: (e) =>
      toast.error(
        errorDetail(e)?.detail ?? apiErrorText(e, "Не удалось закрыть смену"),
      ),
  });
  const session = sessionQ.data;

  // Код, пришедший, пока летел предыдущий запрос. Отправляется следом.
  const queued = useRef<string | null>(null);
  const mutateRef = useRef<(code: string) => void>(() => {});

  const scanMut = useMutation({
    mutationFn: (code: string) => shipment.scanPallet(code),
    onSuccess: (data) => {
      // Плашку кладём ДО записи в кэш экрана: состояние экрана — без
      // «последнего скана» (его несёт только ответ скана).
      const { scanned, ...screen } = data;
      qc.setQueryData(KEY, screen);
      // Паллету даёт ответ, а не сравнение строк: одну паллету сканируют
      // SSCC, `(00)…`, GS1 со служебными символами и старой этикеткой с
      // номером 1С — отсканированная строка с номером в смене не совпадёт.
      // Без неё бэкенд ответить 200 не может; нет — громко, а не пустая плашка.
      if (!scanned) {
        setHero({ kind: "error", code: "error", text: "Сервер не вернул принятую паллету" });
        return;
      }
      setHero({ kind: "ok", pallet: scanned });
    },
    onError: (e) => {
      const detail = errorDetail<shipment.ShipmentErrorDetail>(e);
      setHero(
        detail
          ? { kind: "error", code: detail.code, text: detail.detail }
          : { kind: "error", code: "error", text: apiErrorText(e, "Ошибка скана") },
      );
    },
    onSettled: () => {
      const next = queued.current;
      queued.current = null;
      if (next) mutateRef.current(next);
    },
  });
  mutateRef.current = scanMut.mutate;

  const removeMut = useMutation({
    mutationFn: (id: string) => shipment.removePallet(id),
    onSuccess: (data) => {
      qc.setQueryData(KEY, data);
      toast.success("Паллета снята с погрузки");
    },
    onError: (e) => toast.error(apiErrorText(e, "Не удалось снять паллету")),
  });

  const completeMut = useMutation({
    mutationFn: shipment.completeShipment,
    onSuccess: (result) => {
      setConfirm(false);
      setDone(result);
      qc.invalidateQueries({ queryKey: KEY });
      result.warnings.forEach((w) => toast.warning(w));
    },
    onError: (e) => {
      setConfirm(false);
      setHero({
        kind: "error",
        code: "complete_failed",
        text: errorDetail(e)?.detail ?? apiErrorText(e, "Не удалось завершить смену"),
      });
    },
  });

  const onScan = useCallback(
    (raw: string) => {
      const code = raw.trim();
      if (!code) return;
      // ⚠️ Скан во время загрузки не проглатываем — паллету уже поставили в
      // машину. Пока запрос в полёте, код КОПИТСЯ и уходит сразу после
      // ответа (`onSettled`), а не пропадает.
      if (scanMut.isPending) {
        queued.current = code;
        return;
      }
      scanMut.mutate(code);
    },
    [scanMut],
  );

  // ⚠️ Захват НЕ выключается на время запроса. Раньше в условии стояло
  // `!scanMut.isPending`, и это прямо противоречило комментарию выше: пока
  // запрос летел, КАЖДЫЙ скан пропадал молча — ни ошибки, ни звука, ни строки
  // на экране. А поскольку у запроса не было таймаута, зависший скан оставлял
  // раздел немым НАВСЕГДА, до перезагрузки страницы. Ровно так это и
  // выглядело со склада: «первый паллет считался, дальше никак» (07.09.2026).
  // Перехват выключен только окнами (камера, подтверждение, итог) — не
  // запросом: скан в полёте копится выше. Пойманный при окне код не теряем
  // молча, а говорим, почему он не принят.
  const onBlocked = useCallback(
    (raw: string) => {
      const reason = camera
        ? "Открыта камера — закройте её, чтобы сканировать сканером."
        : confirm
          ? "Открыто подтверждение завершения — закройте его и отсканируйте паллету ещё раз."
          : "Отгрузка завершена — закройте итог и отсканируйте паллету ещё раз.";
      setHero({ kind: "error", code: "error", text: `${reason} Код: ${raw}` });
    },
    [camera, confirm],
  );
  const { scanInputRef, scanInputProps } = useScanCapture({
    enabled: !camera && !confirm && !done,
    onScan,
    onBlocked,
  });
  // Аппаратная «назад» при открытой камере закрывает камеру, а не экран.
  useBackGuard(camera, () => setCamera(false));

  const pending = session?.pending_order_number ?? null;
  const totalPallets = (session?.orders ?? []).reduce((n, o) => n + o.pallets.length, 0);

  return (
    <div className="flex h-app flex-col bg-slate-100">
      <input
        ref={scanInputRef}
        {...scanInputProps}
        className="pointer-events-none fixed left-0 top-0 size-px opacity-0"
      />
      <div className="flex h-[46px] items-center gap-1 bg-slate-900 px-1 text-white">
        <button
          type="button"
          onClick={goBack}
          className="flex size-9 items-center justify-center"
          aria-label="Назад"
        >
          <ArrowLeft className="size-5" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-bold">ОТГРУЗКА</div>
          <div className="truncate text-[10px] text-slate-400">
            {session
              ? `${session.orders.length} заказ(ов) · ${totalPallets} паллет`
              : "загрузка…"}
          </div>
        </div>
      </div>

      {!session ? (
        <div className="flex flex-1 flex-col">
          <LoadState
            isLoading={sessionQ.isLoading}
            error={sessionQ.error}
            onRetry={() => void sessionQ.refetch()}
          />
          {wrongBase && (
            <div className="mx-auto mb-6 max-w-md rounded-md border border-amber-300 bg-amber-50 p-3 text-center">
              <p className="text-[12px] text-amber-900">
                Смену начали с подключением к другой базе 1С. Скан паллет в 1С
                ничего не пишет, поэтому её можно просто закрыть и начать заново
                — уже проведённые документы этому помешают, и портал так и скажет.
              </p>
              <Button
                variant="outline"
                className="mt-2"
                disabled={abandon.isPending}
                onClick={() => abandon.mutate()}
              >
                {abandon.isPending ? "Закрываем…" : "Закрыть смену"}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <>
            <div className="relative min-h-0 flex-1 overflow-y-auto">
              {session.orders.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center text-slate-400">
                  <Truck className="size-10" />
                  <p className="text-[15px]">Сканируйте этикетку паллеты</p>
                  <p className="text-[12px]">
                    Заказ определится сам, и станет видно, сколько в нём паллет
                  </p>
                </div>
              ) : (
                <ul className="divide-y bg-white">
                  {session.orders.map((o) => (
                    <OrderRow
                      key={o.id}
                      order={o}
                      highlight={o.order_number === pending}
                      onRemove={(id) => removeMut.mutate(id)}
                    />
                  ))}
                </ul>
              )}

              {hero.kind !== "idle" && (
                <ScanBannerSlot>
                  <ResultBanner state={hero} onClose={() => setHero({ kind: "idle" })} />
                </ScanBannerSlot>
              )}
            </div>

            <div className="flex gap-1.5 border-t bg-white p-2">
              <Button
                variant="outline"
                className="h-12 flex-1"
                onClick={() => setCamera(true)}
              >
                <Camera className="size-4" /> Камерой
              </Button>
              <Button
                className="h-12 flex-[2]"
                disabled={!session.orders.length || !!pending || completeMut.isPending}
                onClick={() => setConfirm(true)}
              >
                <Check className="size-4" /> Завершить отгрузку
              </Button>
          </div>
        </>
      )}

      {camera && (
        <Suspense fallback={null}>
          <CameraScanner
            formats={QR_FORMATS}
            onDetect={(raw: string) => {
              setCamera(false);
              onScan(raw);
            }}
            onClose={() => setCamera(false)}
          />
        </Suspense>
      )}

      <ConfirmComplete
        open={confirm}
        busy={completeMut.isPending}
        orders={session?.orders.length ?? 0}
        onCancel={() => setConfirm(false)}
        onConfirm={() => completeMut.mutate()}
      />

      <DoneDialog result={done} onClose={() => setDone(null)} />
    </div>
  );
}

function OrderRow({
  order,
  highlight,
  onRemove,
}: {
  order: shipment.ShipmentOrder;
  highlight: boolean;
  onRemove: (id: string) => void;
}): React.ReactElement {
  const left = order.pallets_total - order.pallets.length;
  const full = left <= 0;
  return (
    <li className={cn("px-3 py-2", highlight && "bg-amber-50")}>
      <div className="flex items-baseline gap-2">
        <span className="text-[15px] font-bold">{order.order_number}</span>
        <span
          className={cn(
            "ml-auto text-[13px] font-semibold",
            full ? "text-emerald-700" : "text-amber-700",
          )}
        >
          {order.pallets.length} из {order.pallets_total}
        </span>
      </div>
      <div className="truncate text-[12px] text-slate-500">
        {order.kontragent_name || "—"}
        {order.organization_name ? ` · ${order.organization_name}` : ""}
      </div>
      {!full && (
        <div className="text-[12px] font-medium text-amber-700">
          осталось погрузить: {left}
        </div>
      )}
      <ul className="mt-1 flex flex-wrap gap-1">
        {order.pallets.map((p) => (
          <li
            key={p.id}
            className="flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-[12px]"
          >
            <span className="font-mono tabular-nums">{palletNumber(p)}</span>
            {showArchive(p) && (
              <span className="text-slate-500">№ 1С {p.archive_number}</span>
            )}
            <button
              type="button"
              onClick={() => onRemove(p.id)}
              className="text-slate-400"
              aria-label={`Снять паллету ${palletNumber(p)}`}
            >
              <X className="size-3.5" />
            </button>
          </li>
        ))}
      </ul>
    </li>
  );
}

function ResultBanner({
  state,
  onClose,
}: {
  state: Hero;
  onClose: () => void;
}): React.ReactElement | null {
  if (state.kind === "idle") return null;
  if (state.kind === "error") {
    return (
      <ScanBanner tone="error" title={ERROR_TITLE[state.code]} onClose={onClose}>
        <p className="mt-1 text-[13px] text-red-900">{state.text}</p>
      </ScanBanner>
    );
  }
  const p = state.pallet;
  return (
    <ScanBanner tone="ok" title={`Паллета ${palletNumber(p)}`} onClose={onClose}>
      {showArchive(p) && <p className="text-[12px]">№ 1С {p.archive_number}</p>}
      <p className="text-[12px]">Заказ {p.order_number}</p>
    </ScanBanner>
  );
}

/** Номер паллеты для человека — как на этикетке: SSCC группами по четыре
 *  (`groupSscc`, как везде на портале); у скана до перехода на реестр — номер. */
function palletNumber(p: Pick<shipment.ShipmentPallet, "sscc" | "number">): string {
  return p.sscc ? groupSscc(p.sscc) : p.number;
}

/** «№ 1С …» отдельной строкой — только когда он не повторяет номер: у паллеты
 *  без нашего SSCC номером уже показан её архивный номер (`registry.label`). */
function showArchive(
  p: Pick<shipment.ShipmentPallet, "sscc" | "number" | "archive_number">,
): boolean {
  return !!p.archive_number && (!!p.sscc || p.archive_number !== p.number);
}

function ConfirmComplete({
  open,
  busy,
  orders,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  busy: boolean;
  orders: number;
  onCancel: () => void;
  onConfirm: () => void;
}): React.ReactElement | null {
  if (!open) return null;
  return (
    <Dialog
      title="Завершить отгрузку?"
      body={
        `По ${orders} заказ(ам) расходные будут проведены фактическим временем, ` +
        "а заказы переведены в «Отгружен». В 1С это необратимо."
      }
      confirmLabel="Завершить"
      confirmBusy={busy}
      onConfirm={onConfirm}
      onCancel={onCancel}
      danger
    />
  );
}

function DoneDialog({
  result,
  onClose,
}: {
  result: shipment.CompleteResult | null;
  onClose: () => void;
}): React.ReactElement | null {
  if (!result) return null;
  const lines = [
    result.posted.length ? `Проведены расходные: ${result.posted.join(", ")}` : "",
    result.completed_orders.length
      ? `Отгружены заказы: ${result.completed_orders.join(", ")}`
      : "",
    ...result.warnings,
  ].filter(Boolean);
  return (
    <Dialog
      title="Отгрузка завершена"
      body={lines.join(". ")}
      confirmLabel="Готово"
      onConfirm={onClose}
      onCancel={onClose}
    />
  );
}

export default ShipmentPage;
